import { ApiRequestOptions } from './api';
import { User, UserRole } from './roleUtils';

const TOKEN_STORAGE_KEYS = ['token', 'accessToken', 'auth_token'] as const;
const ROLE_STORAGE_KEY = 'auth_user_role';
const AUTH_SESSION_STORAGE_KEY = 'auth_session_id';
export const AUTH_STORAGE_EVENT = 'auth-storage-change';

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;
export const AUTH_REQUIRED_TOAST_ID = 'auth-required-toast';
export const AUTH_ROLE_TOAST_ID = 'auth-role-toast';

export function getStoredToken(): string | null {
  for (const key of TOKEN_STORAGE_KEYS) {
    const value = localStorage.getItem(key);
    if (value) {
      return value;
    }
  }
  return null;
}

export function setStoredToken(token: string) {
  localStorage.setItem('token', token);
  localStorage.setItem(AUTH_SESSION_STORAGE_KEY, createAuthSessionId());
  notifyAuthStorageChange();
}

export function getStoredAuthSession(): string | undefined {
  return localStorage.getItem(AUTH_SESSION_STORAGE_KEY) || undefined;
}

export type ApiQueryScope = {
  role: UserRole | 'guest';
  session: string;
};

/** Scope object for React Query keys — unique per login session. */
export function getApiQueryScope(): ApiQueryScope {
  const token = getStoredToken();
  if (!token) {
    return { role: 'guest', session: 'guest' };
  }

  const role = getStoredRole() || 'guest';
  let session = getStoredAuthSession();
  if (!session) {
    session = createAuthSessionId();
    localStorage.setItem(AUTH_SESSION_STORAGE_KEY, session);
    notifyAuthStorageChange();
  }

  return { role, session };
}

export function buildApiQueryKey(queryKey: unknown[]) {
  return ['api', getApiQueryScope(), ...queryKey];
}

function createAuthSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function isUserRole(value: unknown): value is UserRole {
  return (
    value === 'SYSTEM_ADMIN' ||
    value === 'PARTNER_ADMIN' ||
    value === 'PARTNER' ||
    value === 'SUPERVISOR' ||
    value === 'CANVASSER'
  );
}

export function isPartnerRole(
  role: UserRole | null | undefined
): role is 'PARTNER' | 'PARTNER_ADMIN' {
  return role === 'PARTNER' || role === 'PARTNER_ADMIN';
}

export function getStoredRole(): UserRole | undefined {
  const role = localStorage.getItem(ROLE_STORAGE_KEY);
  return isUserRole(role) ? role : undefined;
}

export function setStoredRole(role: unknown) {
  if (isUserRole(role)) {
    localStorage.setItem(ROLE_STORAGE_KEY, role);
    notifyAuthStorageChange();
    return;
  }

  localStorage.removeItem(ROLE_STORAGE_KEY);
  notifyAuthStorageChange();
}

export function clearStoredAuth() {
  for (const key of TOKEN_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem(ROLE_STORAGE_KEY);
  localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
  notifyAuthStorageChange();
}

function notifyAuthStorageChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_STORAGE_EVENT));
  }
}

export function getCurrentUserRequest(roleHint?: UserRole): ApiRequestOptions {
  if (roleHint === 'SYSTEM_ADMIN') {
    return {
      method: 'GET',
      url: '/admin/me',
    };
  }

  if (isPartnerRole(roleHint)) {
    return {
      method: 'GET',
      url: '/partner',
    };
  }

  return {
    method: 'GET',
    url: '/user/me',
  };
}

export function getAuthQueryKey(roleHint?: UserRole) {
  return [...AUTH_QUERY_KEY, roleHint || 'user'];
}

export function extractResponseData<T>(response: unknown): T | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  if ('data' in response) {
    return (response as { data?: T }).data;
  }

  return response as T;
}

export function extractUser(response: unknown, roleHint?: UserRole): User | undefined {
  const candidate = extractResponseData<Record<string, unknown>>(response);

  if (!candidate || typeof candidate !== 'object') {
    return undefined;
  }

  if (isUserRole(candidate.role)) {
    return candidate as unknown as User;
  }

  const fallbackRole = getStoredRole();
  if (!fallbackRole) {
    return undefined;
  }

  return {
    ...(candidate as Partial<User>),
    role: fallbackRole,
  } as User;
}

export function extractLoginToken(response: unknown): string | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const nested = extractResponseData<{ token?: string }>(response);
  if (nested?.token) {
    return nested.token;
  }

  if ('token' in response && typeof (response as { token?: unknown }).token === 'string') {
    return (response as { token: string }).token;
  }

  return undefined;
}

export function extractLoginUser(response: unknown): User | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const nested = extractResponseData<{ user?: User }>(response);
  if (nested?.user) {
    return nested.user;
  }

  if ('user' in response && typeof (response as { user?: unknown }).user === 'object') {
    return (response as { user: User }).user;
  }

  return undefined;
}

export function extractLoginRole(response: unknown): UserRole | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const nested = extractResponseData<{ role?: unknown; mode?: unknown; user?: User }>(response);
  const candidateRole = nested?.user?.role ?? nested?.role ?? nested?.mode;

  if (isUserRole(candidateRole)) {
    return candidateRole;
  }

  if ('role' in response && isUserRole((response as { role?: unknown }).role)) {
    return (response as { role: UserRole }).role;
  }

  if ('mode' in response && isUserRole((response as { mode?: unknown }).mode)) {
    return (response as { mode: UserRole }).mode;
  }

  return undefined;
}
